import { useCallback, useEffect, useRef } from 'react'
import { z } from 'zod'
import { StorageKeys } from '@reward-platform/utils/client'
import { getOpCoMetaConfig } from '@reward-platform/platform-config'
import { trimQuotes } from '@reward-platform/shared-schemas'
import { getClientRequestHeader, setClientRequestHeader } from '~/services/clients/nextApiClient'
import { useSessionStorage } from 'usehooks-ts'
import { UserValue } from './User.types'
import { useAuthContext } from '../auth'
import { usePlatformConfig } from '../platform'

const INVALID_COUNTRY_CODE = 'XX'
const UNKNOWN_COUNTRY_OF_RESIDENCE_FALLBACK = 'GB'

const parseString = (value: unknown, defaultValue: string) => {
  const parsedString = z.string().safeParse(value)

  if (!parsedString.success) {
    return defaultValue
  }

  return trimQuotes(parsedString.data)
}

const getCountryCode = (user: UserValue) => {
  const countryFromCountryOfResidence = user.person?.countryOfResidence?.identifier

  // When the country of residence is unknown (missing or XX) use GB, matching TFC country-info,
  // rather than the postal address country (RPS-16447)
  if (!countryFromCountryOfResidence || countryFromCountryOfResidence === INVALID_COUNTRY_CODE) {
    return UNKNOWN_COUNTRY_OF_RESIDENCE_FALLBACK
  }

  return countryFromCountryOfResidence
}

const getResolvedHeaders = ({
  user,
  isAuthenticated,
  countryCodeState,
  currencyCodeState,
}: {
  user?: UserValue
  isAuthenticated: boolean
  countryCodeState: string
  currencyCodeState: string
}) => {
  if (isAuthenticated && user) {
    return {
      countryCode: getCountryCode(user),
      currencyCode: user.person?.currencyCode ?? currencyCodeState,
    }
  }

  return { countryCode: countryCodeState, currencyCode: currencyCodeState }
}

/**
 * Hook to manage user country and currency codes.
 *
 * SSR-safe: Initializes with platform defaults during SSR,
 * then loads from sessionStorage on client mount.
 */
const useUserHeaders = (user?: UserValue) => {
  const { isAuthenticated, isLoading } = useAuthContext()
  const { opCo } = usePlatformConfig()
  const { defaultCountryCode, defaultCurrencyCode } = getOpCoMetaConfig(opCo)

  const [countryCodeState, setCountryCodeState, removeCountryCode] = useSessionStorage(
    StorageKeys.USER_COUNTRY_CODE,
    defaultCountryCode,
    {
      deserializer: (value) => parseString(value, defaultCountryCode),
    }
  )

  const [currencyCodeState, setCurrencyCodeState, removeCurrencyCode] = useSessionStorage(
    StorageKeys.USER_CURRENCY_CODE,
    defaultCurrencyCode,
    {
      deserializer: (value) => parseString(value, defaultCurrencyCode),
    }
  )

  const updateCountryCode = useCallback(
    (value: string) => {
      setCountryCodeState(value)
    },
    [setCountryCodeState]
  )

  const updateCurrencyCode = useCallback(
    (value: string) => {
      setCurrencyCodeState(value)
    },
    [setCurrencyCodeState]
  )

  const resetStorage = useCallback(() => {
    removeCountryCode()
    removeCurrencyCode()
  }, [removeCountryCode, removeCurrencyCode])

  const wasAuthenticatedRef = useRef(false)

  useEffect(() => {
    if (isAuthenticated) {
      wasAuthenticatedRef.current = true
    }
    if (wasAuthenticatedRef.current && !isAuthenticated && !isLoading) {
      resetStorage()
      wasAuthenticatedRef.current = false
    }
  }, [isAuthenticated, isLoading, resetStorage])

  useEffect(() => {
    if (!user || !isAuthenticated) {
      return
    }

    const userCountryCode = getCountryCode(user)
    updateCountryCode(userCountryCode)

    const userCurrencyCode = user.person?.currencyCode
    updateCurrencyCode(userCurrencyCode)
  }, [isAuthenticated, updateCountryCode, updateCurrencyCode, user])

  const { countryCode, currencyCode } = getResolvedHeaders({
    user,
    isAuthenticated,
    countryCodeState,
    currencyCodeState,
  })

  if (!!countryCode && countryCode !== getClientRequestHeader('countryCode')) {
    setClientRequestHeader('countryCode', countryCode)
  }
  if (!!currencyCode && currencyCode !== getClientRequestHeader('currencyCode')) {
    setClientRequestHeader('currencyCode', currencyCode)
  }

  return { countryCode, currencyCode }
}

export default useUserHeaders
